// Admin > Radios > Edit > Radio-Specific Settings > "DMR settings" (IRC380): the codeplug the
// server holds for this one radio. Loading a profile replaces the radio's whole codeplug (all
// radio-local edits) with a snapshot of the profile; editing here changes only this radio. Every
// action in this section is sent at once with its own button - never by the radio editor's Save.
// What is stored here is not proof that the radio has written it to its DMR module: only the
// radio's own apply report says that.
import { useEffect, useRef, useState } from 'react';
import {
  CodeplugApiError,
  clearRadioCodeplug,
  getRadioCodeplug,
  listCodeplugProfiles,
  loadProfileIntoRadio,
  updateRadioCodeplug,
  type Codeplug,
  type CodeplugProfileSummary,
  type RadioCodeplugState,
} from '../../services/dmrCodeplug';
import CodeplugCapture from './CodeplugCapture';
import CodeplugEditor from './CodeplugEditor';
import { applyStatus, codeplugProblems, dmrSettingsKey, loadConfirmText, pickProfile } from './dmrCodeplugModel';

const btn = 'px-3 py-1.5 rounded text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed';

/** The radio's stored codeplug state and the radio's own apply report, stated truthfully. */
export function RadioDmrStatus({ state }: { state: RadioCodeplugState }) {
  const report = state.last_apply_report;
  const status = applyStatus(state);
  const profileMoved =
    state.assigned && state.source_profile_current_revision !== null && state.source_profile_current_revision !== state.source_profile_revision;
  return (
    <div className="space-y-1 text-sm" data-radio-dmr-status>
      {!state.assigned ? (
        <p className="text-gray-300" data-no-codeplug>
          No managed codeplug is stored for this radio. Its DMR module may still hold programming from before, which is not
          shown or changed here. Load a profile to store one for this radio.
        </p>
      ) : (
        <>
          <p className="text-gray-200">
            Stored for this radio from profile <span className="font-semibold">{state.source_profile_name}</span> (profile revision {state.source_profile_revision})
            {state.loaded_at && <> on {new Date(state.loaded_at).toLocaleString()}</>}.
          </p>
          {status === 'queued' && (
            <p className="text-amber-300" data-apply-queued>
              Queued for the radio: it has not yet confirmed writing revision {state.revision} to its DMR module.
            </p>
          )}
          {status === 'applied' && (
            <p className="text-green-300" data-apply-confirmed>The radio confirmed writing revision {state.revision} to its DMR module.</p>
          )}
          {state.locally_modified && <p className="text-amber-300" data-locally-modified>Edited for this radio only since it was loaded.</p>}
          {state.source_profile_id === null && <p className="text-gray-400">The profile has since been deleted; this radio keeps its copy.</p>}
          {profileMoved && (
            <p className="text-amber-300" data-profile-changed>
              The profile has changed since (now revision {state.source_profile_current_revision}). The radio keeps its copy until the profile is loaded again.
            </p>
          )}
        </>
      )}
      <p className="text-xs text-gray-400">Server codeplug revision for this radio: {state.revision}</p>
      <p className="text-xs text-gray-400" data-apply-report>
        {report
          ? `Radio reported revision ${report.revision}: ${report.result === 'APPLIED' ? 'written to the DMR module' : `FAILED${report.error ? ` (${report.error})` : ''}`} on ${new Date(report.reported_at).toLocaleString()}${report.revision !== state.revision ? ' - not the current revision' : ''}.`
          : 'The radio has not reported writing a codeplug to its DMR module. Storing it here does not program the radio.'}
      </p>
    </div>
  );
}

/**
 * Everything in the section belongs to one radio in one network. Switching either remounts it, so
 * no state, selection, draft or late response of the previous radio/network is shown or acted on
 * (React drops updates of the unmounted view), and every write uses the identity it was opened for.
 */
export default function RadioDmrSettings({ radioId, orgId }: { radioId: string; orgId: string }) {
  return <RadioDmrSettingsView key={dmrSettingsKey(radioId, orgId)} radioId={radioId} orgId={orgId} />;
}

export function RadioDmrSettingsView({ radioId, orgId }: { radioId: string; orgId: string }) {
  const [state, setState] = useState<RadioCodeplugState | null>(null);
  const [profiles, setProfiles] = useState<CodeplugProfileSummary[]>([]);
  const [selected, setSelected] = useState('');
  const [draft, setDraft] = useState<Codeplug | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const latest = useRef(0);

  // Only the most recently started refresh may set state (an older one can finish later).
  const refresh = async () => {
    const seq = ++latest.current;
    const [s, p] = await Promise.all([getRadioCodeplug(radioId), orgId ? listCodeplugProfiles(orgId) : Promise.resolve([])]);
    if (seq !== latest.current) return;
    setState(s);
    setProfiles(p);
    setSelected((cur) => pickProfile(cur, s.source_profile_id, p));
  };

  useEffect(() => {
    refresh().catch((e) => setError(e instanceof Error ? e.message : 'Failed to load DMR settings'));
  }, [radioId, orgId]);

  const run = async (fn: () => Promise<string>) => {
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      setMessage(await fn());
    } catch (e) {
      const stale = e instanceof CodeplugApiError && e.code === 'REVISION_CONFLICT';
      if (!stale) {
        setError(e instanceof Error ? e.message : 'Request failed');
      } else {
        // Nothing was changed. Unsaved edits stay in the editor; the status above shows the newer state.
        setError(
          draft
            ? `This radio's codeplug was changed by someone else meanwhile (now revision ${e.currentRevision}); your edits were NOT saved and are still in the editor. Save again to replace that change with your edits, or Discard edits to see the current codeplug.`
            : 'The radio codeplug was changed by someone else meanwhile. It has been reloaded; nothing was changed.'
        );
        await refresh().catch(() => undefined);
      }
    } finally {
      setBusy(false);
    }
  };

  if (!state) return <p className="text-sm text-gray-400">{error ?? 'Loading DMR settings…'}</p>;

  const profile = profiles.find((p) => p.id === selected);
  const load = () => {
    if (!profile || !confirm(loadConfirmText(profile.name, state))) return;
    run(async () => {
      const rev = await loadProfileIntoRadio(radioId, profile.id, state.revision);
      setDraft(null);
      await refresh();
      return `Profile "${profile.name}" stored for this radio (revision ${rev}) and queued for it; the radio has not confirmed writing it to its DMR module yet.`;
    });
  };
  const saveLocal = () =>
    draft &&
    run(async () => {
      const rev = await updateRadioCodeplug(radioId, state.revision, draft);
      setDraft(null);
      await refresh();
      return `This radio's codeplug was stored (revision ${rev}) and queued for it; the radio has not confirmed it yet. The profile and other radios are unchanged.`;
    });
  const clear = () => {
    if (!confirm("Remove the stored codeplug for this radio? The server then holds no managed codeplug for it. This does not erase the radio's DMR module.")) return;
    run(async () => {
      const rev = await clearRadioCodeplug(radioId, state.revision);
      setDraft(null);
      await refresh();
      return `Stored codeplug removed (revision ${rev}). The radio's DMR module is not erased.`;
    });
  };

  return (
    <div className="space-y-3" data-radio-dmr-settings>
      <p className="text-xs text-gray-500">
        Buttons in this section take effect immediately; the radio editor's Save does not load or change the codeplug.
      </p>
      <RadioDmrStatus state={state} />
      {error && <p className="text-sm text-red-300" role="alert">{error}</p>}
      {message && <p className="text-sm text-green-300">{message}</p>}

      <div className="flex flex-wrap items-center gap-2">
        <select className="px-2 py-1.5 bg-gray-900 border border-gray-600 rounded text-white text-sm" value={selected} disabled={busy || profiles.length === 0}
          onChange={(e) => setSelected(e.target.value)} aria-label="Codeplug profile">
          {profiles.length === 0 && <option value="">No profiles in this network (Admin &gt; General)</option>}
          {profiles.map((p) => <option key={p.id} value={p.id}>{p.name} (revision {p.revision})</option>)}
        </select>
        <button type="button" className={`${btn} bg-amber-700 hover:bg-amber-600 text-white`} disabled={busy || !profile || draft !== null} onClick={load} data-load-profile>
          {state.assigned ? 'Load profile (replaces radio codeplug)…' : 'Load profile…'}
        </button>
      </div>

      {state.assigned && !draft && (
        <div className="flex flex-wrap gap-2">
          <button type="button" className={`${btn} bg-blue-700 hover:bg-blue-600 text-white`} disabled={busy} onClick={() => state.codeplug && setDraft(state.codeplug)}>
            Edit this radio's codeplug
          </button>
          <button type="button" className={`${btn} bg-gray-700 hover:bg-gray-600 text-white`} disabled={busy} onClick={clear}>Remove codeplug</button>
        </div>
      )}

      {draft && (
        <div className="space-y-2 border border-blue-800 rounded p-3" data-radio-codeplug-editor>
          <p className="text-sm text-blue-200">Editing this radio's own copy. The profile and other radios are not changed; loading a profile later replaces these edits.</p>
          <CodeplugEditor value={draft} onChange={setDraft} />
          <div className="flex gap-2">
            <button type="button" className={`${btn} bg-blue-600 hover:bg-blue-500 text-white`} disabled={busy || codeplugProblems(draft).length > 0} onClick={saveLocal}>
              Save radio codeplug
            </button>
            <button type="button" className={`${btn} bg-gray-700 hover:bg-gray-600 text-white`} disabled={busy} onClick={() => setDraft(null)}>Discard edits</button>
          </div>
        </div>
      )}

      {/* Reads the radio's loaded codeplug into a NEW profile only; it never loads or writes a codeplug. */}
      <CodeplugCapture radioId={radioId} orgId={orgId} sectionBusy={busy} draftOpen={draft !== null}
        onSaved={() => refresh().catch(() => undefined)} />
    </div>
  );
}
